<template>
  <ContentWrapper
    :create-icon="can('CREATE_EQUIPMENT_PARTS') ? 'add-equipment-specification' : null"
    :header-tag="headerTag"
    search-input-id="admin-equipment-specification-search-phrase"
    :show-back="showBack"
    title="Equipment Specifications"
    @create="handleCreate"
    @search="handleSearch"
  >
    <EquipmentSpecificationsTable
      class="admin-table"
      :specifications="filteredEquipmentSpecifications"
      :selected-id="specification.id"
      @select="handleSelect"
    />

    <section class="admin-form-wrapper">
      <content-header
        class="admin-form-header"
        tag="h2"
        :title="specification.id ? `Edit - ${specification.name}` : 'Create Specification'"
      />

      <span class="admin-form-tab">
        <img alt="Equipment Specifications" src="../../../assets/icons/equipment-specification.svg" />
      </span>

      <EquipmentSpecificationsForm
        class="admin-form"
        :disabled="specification.name === undefined"
        :specification="specification"
        @cancel="handleCancel"
        @delete="handleDelete"
        @save="handleSave"
      />
    </section>
  </ContentWrapper>
</template>

<script>
import ContentWrapper from "./ContentWrapper";
import EquipmentSpecificationsTable from "../../../components/admin/EquipmentSpecificationsTable";
import EquipmentSpecificationsForm from "../../../components/admin/EquipmentSpecificationsForm";

import { getEquipmentSpecifications } from "../../../api/EquipmentAPI";

export default {
  data() {
    return {
      specification: {},
      _specifications: [],
      search: "",
    };
  },

  props: {
    headerTag: {
      type: String,
      default: undefined,
    },
    id: {
      type: [Number, String],
      default: undefined,
    },
    routeParams: {
      type: Object,
      default: undefined,
    },
    showBack: {
      type: Boolean,
      default: true,
    },
    specifications: {
      type: Array,
      default: undefined,
    },
  },

  computed: {
    filteredEquipmentSpecifications() {
      const regex = this.getFilterRegex(this.search);

      return this.$data._specifications.filter((s) =>
        regex.test(s?.name ?? "")
      );
    },
  },

  methods: {
    handleCancel() {
      if (this.confirmIfDirty()) {
        this.specification = {};
      }
    },
    handleCreate() {
      if (this.confirmIfDirty()) {
        const ranks = this.$data._specifications.reduce(
          (map, spec) => ({
            ...map,
            ...spec.assignments.reduce(
              (map, assign) => ({
                ...map,
                [assign.subcategory?.id]: Math.max(
                  map[assign.subcategory?.id] ?? 0,
                  assign.rank
                ),
              }),
              {}
            ),
          }),
          {}
        );

        const tpl = this.$data._specifications[0];

        this.specification = {
          ...tpl,
          assignments: [...tpl.assignments].map((a) => ({
            ...a,
            rank: ranks[a.subcategory?.id] + 1,
          })),
          id: undefined,
          name: "",
          name_fr: null,
          options: [],
          protected: false,
        };
      }
    },
    handleDelete(specification) {
      this.$data._specifications = this.$data._specifications.filter(
        (s) => s?.id !== specification?.id
      );

      this.specification = {};
    },
    handleSave(specification) {
      const created = !this.specification.id;

      this.$data._specifications = [
        ...this.$data._specifications.filter(
          (s) => s?.id !== specification?.id
        ),
        specification,
      ];

      this.handleSelect(created ? specification : undefined, true);

      this.showAlert(
        `Your equipment specification has been ${
          created ? "created" : "updated"
        } successfully!`
      );
    },
    handleSearch(phrase) {
      this.search = phrase;
    },
    handleSelect(specification, force = false) {
      if (force || this.confirmIfDirty()) {
        this.specification = {
          ...this.$data._specifications.find(
            (s) => s?.id === specification?.id
          ),
        };
      }
    },
  },

  watch: {
    "$data._specifications"(to, from) {
      if (to !== from) {
        this.$data._specifications = to.sort(this.sortBy("id", "name"));
        this.$emit("update:specifications", this.$data._specifications);
      }
    },
    async specifications(to, from) {
      to === this.$data._specifications ||
        (this.$data._specifications =
          to?.slice?.() ?? (await getEquipmentSpecifications()));
    },
    "specification.id"(to, from) {
      to === this.id ||
        this.$nextTick(() => {
          this.$router.push({
            ...this.$route,
            params: { ...this.$route.params, ...this.routeParams, id: to },
          });
        });
    },
  },

  async created() {
    this.$data._specifications =
      this.specifications?.slice?.() ?? (await getEquipmentSpecifications());

    this.handleSelect(this);
  },

  beforeRouteUpdate(to, from, next) {
    if (!(this?.confirmIfDirty?.() ?? true)) {
      return next(false);
    }

    this.abortRequests();
    next();
  },

  beforeRouteLeave(to, from, next) {
    if (!to.params.skipDirtyCheck && !(this?.confirmIfDirty?.() ?? true)) {
      next(false);
    }

    this.abortRequests();
    next();
  },

  components: {
    ContentWrapper,
    EquipmentSpecificationsTable,
    EquipmentSpecificationsForm,
  },
};
</script>